/**
 * Стор для пользовательской настройки меню sidebar.
 * Хранит порядок и видимость пунктов в localStorage.
 */
import { create } from 'zustand';

export interface MenuItemConfig {
  key: string;
  visible: boolean;
  children?: MenuItemConfig[];
}

interface MenuConfigStore {
  configs: MenuItemConfig[];
  loaded: boolean;
  load: (tenantId?: number) => void;
  save: (tenantId?: number) => void;
  setConfigs: (configs: MenuItemConfig[]) => void;
  toggleVisibility: (key: string, parentKey?: string) => void;
  moveItem: (fromIndex: number, toIndex: number) => void;
  moveChild: (parentKey: string, fromIndex: number, toIndex: number) => void;
  reset: (tenantId?: number) => void;
}

const storageKey = (tid?: number) => `menu_config${tid ? `_${tid}` : ''}`;

export const useMenuConfigStore = create<MenuConfigStore>((set, get) => ({
  configs: [],
  loaded: false,

  load: (tenantId?: number) => {
    const raw = localStorage.getItem(storageKey(tenantId));
    if (raw) {
      try {
        set({ configs: JSON.parse(raw), loaded: true });
        return;
      } catch { /* невалидный JSON — сбрасываем */ }
    }
    set({ configs: [], loaded: true });
  },

  save: (tenantId?: number) => {
    localStorage.setItem(storageKey(tenantId), JSON.stringify(get().configs));
  },

  setConfigs: (configs) => set({ configs }),

  toggleVisibility: (key, parentKey) => {
    const { configs } = get();
    if (parentKey) {
      set({
        configs: configs.map(c =>
          c.key === parentKey && c.children
            ? { ...c, children: c.children.map(ch => ch.key === key ? { ...ch, visible: !ch.visible } : ch) }
            : c
        ),
      });
    } else {
      set({ configs: configs.map(c => c.key === key ? { ...c, visible: !c.visible } : c) });
    }
  },

  moveItem: (fromIndex, toIndex) => {
    const updated = [...get().configs];
    const [moved] = updated.splice(fromIndex, 1);
    updated.splice(toIndex, 0, moved);
    set({ configs: updated });
  },

  moveChild: (parentKey, fromIndex, toIndex) => {
    set({
      configs: get().configs.map(c => {
        if (c.key === parentKey && c.children) {
          const children = [...c.children];
          const [moved] = children.splice(fromIndex, 1);
          children.splice(toIndex, 0, moved);
          return { ...c, children };
        }
        return c;
      }),
    });
  },

  reset: (tenantId?: number) => {
    localStorage.removeItem(storageKey(tenantId));
    set({ configs: [], loaded: true });
  },
}));
